import { useEffect, useMemo, useState, type MouseEvent } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import iconFilters from "../../../assets/icon-filters.svg";
import { useBrandThemeColors } from "../../../branding";
import { useAssessmentNameOptions } from "../../../hooks/useAssessmentNameOptions";

export type EnhancedTaggingRuleFilterDimension = "assessmentName";

export type EnhancedTaggingRuleAppliedFilters = Readonly<{
  assessmentNames: string[];
}>;

export const EMPTY_ENHANCED_TAGGING_RULE_FILTERS: EnhancedTaggingRuleAppliedFilters =
  {
    assessmentNames: [],
  };

type EnhancedTaggingRuleFilterBarProps = Readonly<{
  searchText: string;
  onSearchTextChange: (value: string) => void;
  appliedFilters: EnhancedTaggingRuleAppliedFilters;
  onAppliedFiltersChange: (next: EnhancedTaggingRuleAppliedFilters) => void;
}>;

const FILTER_DIMENSIONS: ReadonlyArray<{
  id: EnhancedTaggingRuleFilterDimension;
  label: string;
}> = [{ id: "assessmentName", label: "Assessment name" }];

const COLUMN_HEIGHT = 260;

function countSelections(filters: EnhancedTaggingRuleAppliedFilters): number {
  return filters.assessmentNames.length;
}

function selectSingle(list: string[], code: string): string[] {
  return list.includes(code) ? [] : [code];
}

export default function EnhancedTaggingRuleFilterBar({
  searchText,
  onSearchTextChange,
  appliedFilters,
  onAppliedFiltersChange,
}: EnhancedTaggingRuleFilterBarProps) {
  const { primaryColor } = useBrandThemeColors();
  const { options: assessmentNameOptions, loading: namesLoading } =
    useAssessmentNameOptions();

  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);
  const [panelSearch, setPanelSearch] = useState("");
  const [draftFilters, setDraftFilters] =
    useState<EnhancedTaggingRuleAppliedFilters>(appliedFilters);

  const filtersOpen = Boolean(filtersAnchor);
  const activeMeta = FILTER_DIMENSIONS[0];

  useEffect(() => {
    if (!filtersOpen) return;
    setDraftFilters(appliedFilters);
    setPanelSearch("");
  }, [appliedFilters, filtersOpen]);

  const activeFilterCount = countSelections(appliedFilters);

  const nameOptions = useMemo(
    () =>
      assessmentNameOptions.map((option) => ({
        code: option.name,
        name: option.name,
      })),
    [assessmentNameOptions],
  );

  const filteredValueOptions = useMemo(() => {
    const q = panelSearch.trim().toLowerCase();
    if (!q) return nameOptions;
    return nameOptions.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q),
    );
  }, [nameOptions, panelSearch]);

  const handleOpenFilters = (event: MouseEvent<HTMLElement>) => {
    setFiltersAnchor(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setFiltersAnchor(null);
  };

  const handleToggleValue = (code: string) => {
    setDraftFilters((prev) => ({
      ...prev,
      assessmentNames: selectSingle(prev.assessmentNames, code),
    }));
  };

  const handleClearAll = () => {
    setDraftFilters(EMPTY_ENHANCED_TAGGING_RULE_FILTERS);
    setPanelSearch("");
  };

  const handleConfirm = () => {
    onAppliedFiltersChange(draftFilters);
    handleCloseFilters();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: { xs: "wrap", md: "nowrap" },
        width: "100%",
      }}
    >
      <TextField
        size="small"
        value={searchText}
        onChange={(event) => onSearchTextChange(event.target.value)}
        placeholder="Input Assessment name..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
            </InputAdornment>
          ),
        }}
        sx={{
          flex: 1,
          minWidth: 240,
          "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: "6px",
            bgcolor: "#fff",
          },
        }}
      />

      <Button
        variant="outlined"
        onClick={handleOpenFilters}
        startIcon={
          <Box
            component="img"
            src={iconFilters}
            alt=""
            sx={{ width: 14, height: 14, display: "block" }}
          />
        }
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
        aria-haspopup="dialog"
        aria-expanded={filtersOpen}
        sx={{
          height: 40,
          minWidth: 136,
          px: 1.5,
          borderRadius: "6px",
          borderColor: "#D1D5DB",
          color: "#374151",
          textTransform: "none",
          fontWeight: 500,
          bgcolor: activeFilterCount > 0 ? "#F3F4F6" : "#fff",
          "&:hover": {
            borderColor: "#9CA3AF",
            bgcolor: "#F9FAFB",
          },
        }}
      >
        Filters
        {activeFilterCount > 0 ? (
          <Box
            component="span"
            sx={{
              ml: 1,
              minWidth: 20,
              height: 20,
              px: 0.5,
              borderRadius: "10px",
              bgcolor: primaryColor,
              color: "#fff",
              fontSize: 12,
              lineHeight: "20px",
              textAlign: "center",
            }}
          >
            {activeFilterCount}
          </Box>
        ) : null}
      </Button>

      <Popover
        open={filtersOpen}
        anchorEl={filtersAnchor}
        onClose={handleCloseFilters}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 520,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: "8px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: "1px solid #E5E7EB" }}>
          <TextField
            size="small"
            fullWidth
            value={panelSearch}
            onChange={(event) => setPanelSearch(event.target.value)}
            placeholder="Input dimension or value..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "200px 1fr",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              borderRight: "1px solid #E5E7EB",
              overflowY: "auto",
              maxHeight: COLUMN_HEIGHT,
            }}
          >
            {FILTER_DIMENSIONS.map((dimension) => {
              const count = draftFilters.assessmentNames.length;
              return (
                <Box
                  key={dimension.id}
                  component="button"
                  type="button"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                    minHeight: 44,
                    border: "none",
                    borderLeft: `3px solid ${primaryColor}`,
                    textAlign: "left",
                    px: 2,
                    py: 1.25,
                    cursor: "default",
                    bgcolor: `${primaryColor}14`,
                    color: primaryColor,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dimension.label}
                  </Box>
                  {count > 0 ? (
                    <Box
                      component="span"
                      sx={{
                        minWidth: 22,
                        height: 19,
                        px: 0.75,
                        borderRadius: "10px",
                        bgcolor: `${primaryColor}0F`,
                        color: primaryColor,
                        fontSize: 12,
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {count}
                    </Box>
                  ) : null}
                  <ChevronRightIcon
                    sx={{ fontSize: 16, color: primaryColor }}
                  />
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              maxHeight: COLUMN_HEIGHT,
            }}
          >
            <Typography
              sx={{
                px: 2,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "#374151",
                borderBottom: "1px solid #E5E7EB",
                flexShrink: 0,
              }}
            >
              {activeMeta.label}
            </Typography>
            <Box sx={{ overflowY: "auto", flex: 1, px: 0.5 }}>
              {namesLoading ? (
                <Typography
                  sx={{ p: 1.5, fontSize: "0.8125rem", color: "#9CA3AF" }}
                >
                  Loading…
                </Typography>
              ) : filteredValueOptions.length === 0 ? (
                <Typography
                  sx={{ p: 1.5, fontSize: "0.8125rem", color: "#9CA3AF" }}
                >
                  No options
                </Typography>
              ) : (
                filteredValueOptions.map((option) => {
                  const checked = draftFilters.assessmentNames.includes(
                    option.code,
                  );
                  return (
                    <Box
                      key={option.code}
                      role="checkbox"
                      aria-checked={checked}
                      tabIndex={0}
                      onClick={() => handleToggleValue(option.code)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleToggleValue(option.code);
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                        minHeight: 44,
                        border: "none",
                        bgcolor: "transparent",
                        px: 1.5,
                        py: 1,
                        cursor: "pointer",
                        textAlign: "left",
                        "&:hover": { bgcolor: "#F9FAFB" },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        tabIndex={-1}
                        sx={{ p: 0.5, pointerEvents: "none" }}
                      />
                      <Typography
                        sx={{ fontSize: "0.875rem", color: "#374151" }}
                      >
                        {option.name}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 1,
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <Button
            size="small"
            onClick={handleClearAll}
            startIcon={<FilterAltOffOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              color: "#EF4444",
              fontWeight: 500,
            }}
          >
            Clear all
          </Button>
          <IconButton
            aria-label="Apply filters"
            onClick={handleConfirm}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              bgcolor: primaryColor,
              color: "#fff",
              "&:hover": { bgcolor: primaryColor },
            }}
          >
            <CheckIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Popover>
    </Box>
  );
}
